import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Label } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { IMAGES, PROJECTS, PROJECT_CATEGORIES } from "@/lib/data";

const TABS = ["All", ...PROJECT_CATEGORIES];

const ProjectCard = ({ p, i }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
    className="group"
    data-testid={`project-card-${i}`}
  >
    <div className="relative overflow-hidden aspect-[4/3]">
      <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
      {p.status === "In Progress" && (
        <span className="absolute top-0 left-0 bg-brand text-white text-[10px] tracking-[0.15em] uppercase font-bold px-4 py-2">
          In Progress
        </span>
      )}
    </div>
    <h3 className="font-heading uppercase text-navy font-semibold mt-4 text-lg leading-tight">{p.title}</h3>
    <p className="text-xs text-brand mt-1 tracking-wide uppercase">{p.city}</p>
    <p className="text-sm text-navy/60 leading-relaxed mt-3">{p.desc}</p>
    <Link to="/contact" data-testid={`project-view-${i}`}
          className="group/link inline-flex items-center gap-2 mt-4 text-xs tracking-[0.15em] uppercase font-bold text-navy hover:text-brand transition-colors">
      View Project <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
    </Link>
  </motion.div>
);

export default function Projects() {
  const [tab, setTab] = useState("All");
  const [city, setCity] = useState("All Cities");

  const cities = useMemo(() => ["All Cities", ...Array.from(new Set(PROJECTS.map((p) => p.city)))], []);

  const filtered = useMemo(
    () => PROJECTS.filter((p) => (tab === "All" || p.category === tab) && (city === "All Cities" || p.city === city)),
    [tab, city]
  );

  const current = filtered.filter((p) => p.status === "In Progress");
  const completed = filtered.filter((p) => p.status === "Completed");

  return (
    <>
      <PageHero
        label="Projects"
        titleLines={["Building better", "outcomes through", "science and expertise."]}
        description="We partner with our clients across the Lower Mainland and beyond to deliver high-performance building enclosures that are durable, efficient, and sustainable."
        image={IMAGES.projectsHero}
        imageAlt="Modern building"
      />

      <section className="bg-white" data-testid="projects-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
          {/* Category tabs */}
          <div className="flex flex-wrap gap-2 border-b border-border pb-6 mb-6 overflow-x-auto no-scrollbar">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-6 py-3 text-sm font-semibold whitespace-nowrap transition-colors ${
                  tab === t ? "bg-brand text-white" : "text-navy/70 hover:text-navy"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* City filter */}
          <div className="flex items-center gap-4 mb-14">
            <label className="text-xs tracking-[0.18em] uppercase font-bold text-navy/60">Filter By City</label>
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              data-testid="city-filter"
              className="border border-border px-4 py-2.5 text-sm text-navy bg-white focus:border-brand outline-none min-w-[200px]"
            >
              {cities.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>

          <AnimatePresence mode="popLayout">
            {current.length > 0 && (
              <div className="mb-16" key="current">
                <Label className="mb-6 block">Current Projects</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-4">
                  {current.map((p, i) => <ProjectCard key={p.title} p={p} i={i} />)}
                </div>
              </div>
            )}

            {completed.length > 0 && (
              <div key="completed">
                <Label className="mb-6 block">Completed Projects</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-4">
                  {completed.map((p, i) => <ProjectCard key={p.title} p={p} i={i} />)}
                </div>
              </div>
            )}

            {filtered.length === 0 && (
              <p key="empty" className="text-center text-navy/50 py-16" data-testid="no-projects">
                No projects match the selected filters.
              </p>
            )}
          </AnimatePresence>
        </div>
      </section>

      <CTASection />
    </>
  );
}
